import type { MutableRefObject, Ref } from "react";
import { HTMLProps, ReactNode, useCallback, useRef } from "react";
import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  useEffect,
  useState,
} from "react";
import { Placement } from "@floating-ui/dom";
import { arrow, FloatingArrow } from "@floating-ui/react";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react-dom-interactions";

import cn from "@/utils/cn";

export interface Props {
  content: ReactNode | ((close: () => void) => ReactNode);
  placement?: Placement;
  showOn?: "click" | "hover";
  delay?:
    | number
    | Partial<{
        open: number;
        close: number;
      }>;
  className?: string;
  children?: ReactNode;
  onClose?: () => void;
  openByDefault?: boolean;
  stopClickPropagation?: boolean;
  mode?: string;
}

export const Tooltip = ({
  content,
  placement = "top",
  showOn = "hover",
  delay = { open: 100, close: 700 },
  children,
  className,
  onClose,
  openByDefault = false,
  stopClickPropagation = false,
  mode = "dark",
  ...props
}: Props) => {
  const totalChildrenCount = Children.count(children);
  if (totalChildrenCount > 1) {
    throw new Error("Tooltip component must only have one child.");
  }

  const [open, setOpen] = useState(openByDefault);
  const arrowRef = useRef(null);
  const updateOpenState = (newState: boolean) => {
    if (!newState && onClose) {
      onClose();
    }
    setOpen(newState);
  };

  const {
    x,
    y,
    reference,
    floating,
    strategy,
    context,
    refs,
    update,
    placement: finalPlacement,
  } = useFloating({
    placement,
    open,
    onOpenChange: updateOpenState,
    middleware: [
      offset(10),
      flip(),
      shift({
        padding: {
          right: 18,
          left: 18,
        },
      }),
      arrow({
        element: arrowRef,
      }),
    ],
    strategy: "fixed",
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useRole(context, { role: "tooltip" }),
    useDismiss(context),
    useClick(context, {
      enabled: showOn === "click",
    }),
    useHover(context, {
      enabled: showOn === "hover",
      delay,
    }),
    useFocus(context, {
      enabled: showOn === "hover",
    }),
  ]);

  const assignRef = <T,>(ref: Ref<T> | undefined, value: T | null) => {
    if (typeof ref === "function") {
      ref(value);
    } else if (ref) {
      (ref as MutableRefObject<T | null>).current = value;
    }
  };

  const childRef =
    isValidElement(children) && "ref" in children
      ? (children as React.ReactElement & { ref?: Ref<Element> }).ref
      : null;
  const mergedRef = useCallback(
    (node: Element | null) => {
      reference(node);
      assignRef(childRef, node);
    },
    [reference, childRef],
  );

  const userProps: HTMLProps<Element> | undefined = { ref: mergedRef };
  if (stopClickPropagation) {
    userProps.onClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setOpen(!open);
    };
  }

  useEffect(() => {
    if (refs.reference.current && refs.floating.current && open) {
      return autoUpdate(refs.reference.current, refs.floating.current, update);
    }
  }, [refs.reference, refs.floating, update, open]);

  if (mode == "light") {
    className =
      className +
      " " +
      "bg-white border-[#F7F7F8] shadow-[0px_4px_40px_0px_#3C404314]";
  } else {
    className = className + " " + "bg-chorus-black-500";
  }

  return (
    <Fragment>
      {isValidElement(children) &&
        cloneElement(children, getReferenceProps(userProps))}
      {open && (
        <div
          data-testid="tooltip"
          className={cn(
            "z-50 flex min-w-[80px] items-center justify-center gap-10 rounded-sm  px-2 pb-[5px] pt-[3px] font-manrope text-xxs font-medium leading-4 tracking-[0.24px] text-chorus-gray-300 shadow-box-3",
            "data-[placement=top-end]:animate-slide-down data-[placement=top-start]:animate-slide-down data-[placement=top]:animate-slide-down",
            "data-[placement=bottom-end]:animate-slide-up data-[placement=bottom-start]:animate-slide-up data-[placement=bottom]:animate-slide-up",
            "data-[placement=right-end]:animate-slide-left data-[placement=right-start]:animate-slide-left data-[placement=right]:animate-slide-left",
            "data-[placement=left-end]:animate-slide-right data-[placement=left-start]:animate-slide-right data-[placement=left]:animate-slide-right",
            className,
          )}
          {...getFloatingProps({
            ref: floating,
            ...props,
            style: {
              position: strategy,
              top: y ?? "",
              left: x ?? "",
            },
          })}
          data-placement={finalPlacement}
        >
          {typeof content === "function"
            ? content(() => {
                updateOpenState(false);
                (refs.reference.current as HTMLElement).focus();
              })
            : content}
          <FloatingArrow
            ref={arrowRef}
            // @ts-ignore
            context={context}
            width={10}
            height={5}
            staticOffset={
              finalPlacement.includes("start") || finalPlacement.includes("end")
                ? "15%"
                : null
            }
          />
        </div>
      )}
    </Fragment>
  );
};

Tooltip.displayName = "Tooltip";
